type ElementMapping = {
	source: HTMLElement;
	clone: HTMLElement;
	column: number;
};

type ColumnLookup = Record<number, Record<number, number>>;

// stolen from jquery.tablesorter
function computeTableHeaderCellIndexes(t: HTMLTableElement): ColumnLookup {
	const matrix: Array<Array<undefined|"x">> = [];
	const lookup: ColumnLookup = { };
	const thead = t.getElementsByTagName("THEAD")[0] as HTMLTableSectionElement;
	const trs = thead.getElementsByTagName("TR") as HTMLCollectionOf<HTMLTableRowElement>;
	let firstAvailCol: number|undefined;

	for (let i = 0; i < trs.length; i++) {
		const tr = trs[i];
		const rowIndex = tr.rowIndex;
		for (let j = 0; j < tr.cells.length; j++) {
			const c = tr.cells[j];
			const rowSpan = c.rowSpan || 1;
			const colSpan = c.colSpan || 1;

			if (! matrix.hasOwnProperty(rowIndex)) {
				matrix[rowIndex] = [];
			}
			// Find first available column in the first row
			for (let k = 0; k < matrix[rowIndex].length + 1; k++) {
				if (! matrix[rowIndex].hasOwnProperty(k)) {
					firstAvailCol = k;
					break;
				}
			}
			if (! lookup.hasOwnProperty(rowIndex)) {
				lookup[rowIndex] = { };
			}

			// @TODO--- these `as number`s here should be vetted
			lookup[rowIndex][c.cellIndex] = (firstAvailCol as number);
			for (let k = rowIndex; k < rowIndex + rowSpan; k++) {
				if (! matrix.hasOwnProperty(k)) {
					matrix[k] = [];
				}
				for (let l = (firstAvailCol as number); l < (firstAvailCol as number) + colSpan; l++) {
					matrix[k][l] = "x";
				}
			}
		}
	}
	return lookup;
}

export class TableHeaderClone {

	public readonly element: HTMLElement = document.createElement("table");
	private elementMap: ElementMapping[] = [];
	private headerHeightValue?: number;

	constructor(private table: HTMLTableElement) {
		this.element.className = table.className;
		this.initialize();
	}

	public get headerHeight(): number|undefined {
		return this.headerHeightValue;
	}

	public resize(): void {
		let maxHeight = -Infinity;

		const parentPos = this.table.getBoundingClientRect();

		for (let i = 0; i < this.elementMap.length; i++) {

			const { clone, source } = this.elementMap[i];

			const pos = source.getBoundingClientRect();
			const top = pos.top - parentPos.top;

			maxHeight = Math.max(pos.height + top, maxHeight);

			clone.style.width = `${pos.width}px`;
			clone.style.height = `${pos.height}px`;

			if (source.hasAttribute("tabindex")) {
				clone.setAttribute("tabindex", source.getAttribute("tabindex") as string);
			}
			clone.className = source.className;
		}

		this.element.style.width = `${parentPos.width}px`;
		this.element.style.height = `${maxHeight}px`;

		this.headerHeightValue = maxHeight;
	}

	private forwardEvents(clone: HTMLElement, source: HTMLElement): void {
		const forwardMouseEvent = (e: MouseEvent) => {
			const event = document.createEvent("MouseEvent");
			event.initMouseEvent(e.type, true, true, window, 0, e.screenX, e.screenY, e.clientX, e.clientY, e.ctrlKey, e.altKey, e.shiftKey, e.metaKey, e.button, e.relatedTarget);
			source.dispatchEvent(event);

			this.resize();
		};
		clone.addEventListener("mousemove", forwardMouseEvent);
		clone.addEventListener("mouseenter", forwardMouseEvent);
		clone.addEventListener("mouseleave", forwardMouseEvent);
		clone.addEventListener("mouseover", forwardMouseEvent);
		clone.addEventListener("mouseout", forwardMouseEvent);
		clone.addEventListener("click", forwardMouseEvent);
		if (window.KeyboardEvent) {
			clone.addEventListener("keydown", (event) => {
				const keyEvent = new window.KeyboardEvent("keydown", event);
				source.dispatchEvent(keyEvent);
				this.resize();
			});
		}
	}

	private initialize(): void {
		const thead = this.table.querySelector("thead") as HTMLTableSectionElement;

		const thColumns = computeTableHeaderCellIndexes(this.table);

		const fragment = document.createDocumentFragment();
		const childTrs = thead.getElementsByTagName("tr") as HTMLCollectionOf<HTMLTableRowElement>;
		for (let j = 0; j < childTrs.length; j++) {
			const childThs = childTrs[j].getElementsByTagName("th") as HTMLCollectionOf<HTMLTableHeaderCellElement>;
			const cloneTr = document.createElement("tr");
			cloneTr.className = childTrs[j].className;
			for (let i = 0; i < childThs.length; i++) {
				const source = childThs[i];
				const clone = document.createElement("th");
				clone.rowSpan   = source.rowSpan;
				clone.colSpan   = source.colSpan;
				clone.className = source.className;
				while (source.firstChild) {
					clone.appendChild(source.firstChild);
				}
				source.innerHTML = clone.innerHTML;
				this.elementMap.push({
					source,
					clone,
					column: thColumns[(source.parentNode as HTMLTableRowElement).rowIndex][source.cellIndex]
				});

				cloneTr.appendChild(clone);
				this.forwardEvents(clone, source);
			}
			fragment.appendChild(cloneTr);
		}

		this.element.appendChild(fragment);
		this.resize();
	}
}
